import { useState, useEffect } from "react";
import { AlertTriangle, CheckCircle, RefreshCw, MapPin } from "lucide-react";
import { useAuthStore } from "../../state/auth";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import type { LangCode } from "../../i18n";

interface Violation {
  id: string;
  serial_number: string;
  geofence_name: string;
  geofence_id: string;
  created_at: string;
  gps_lat: number;
  gps_lng: number;
  acknowledged: boolean;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
}

interface Geofence {
  id: string;
  name: string;
}

export function ViolationsList() {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  const [violations, setViolations] = useState<Violation[]>([]);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedGeofenceId, setSelectedGeofenceId] = useState<string>("");
  const [acknowledging, setAcknowledging] = useState<Set<string>>(new Set());

  const fetchViolations = async () => {
    if (!user) return;
    setRefreshing(true);
    try {
      const params = new URLSearchParams({ role: user.role });
      if (user.hospital_id) params.append("hospital_id", user.hospital_id);
      if (user.main_site_id) params.append("main_site_id", user.main_site_id);
      if (selectedGeofenceId)
        params.append("geofence_id", selectedGeofenceId);

      params.append("action", "violations");
      const response = await fetch(
        `/api/geofences?${params.toString()}`,
        {
          headers: getAuthHeaders(user),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setViolations(data);
      }
    } catch (error) {
      console.error("Failed to fetch violations:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchGeofences = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams({ role: user.role });
      if (user.hospital_id) params.append("hospital_id", user.hospital_id);
      if (user.main_site_id) params.append("main_site_id", user.main_site_id);

      const response = await fetch(`/api/geofences?${params.toString()}`, {
        headers: getAuthHeaders(user),
      });

      if (response.ok) {
        const data = await response.json();
        setGeofences(data);
      }
    } catch (error) {
      console.error("Failed to fetch geofences:", error);
    }
  };

  useEffect(() => {
    fetchGeofences();
    fetchViolations();
  }, [user]);

  useEffect(() => {
    fetchViolations();
  }, [selectedGeofenceId]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchViolations();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [selectedGeofenceId, user]);

  const handleAcknowledge = async (violationId: string) => {
    if (!user) return;

    setAcknowledging((prev) => new Set(prev).add(violationId));
    try {
      const response = await fetch(`/api/geofences?action=violations`, {
        method: "POST",
        headers: getAuthHeaders(user),
        body: JSON.stringify({ violation_id: violationId }),
      });

      if (response.ok) {
        setViolations(
          violations.map((v) =>
            v.id === violationId ? { ...v, acknowledged: true } : v
          )
        );
      }
    } catch (error) {
      console.error("Failed to acknowledge violation:", error);
    } finally {
      setAcknowledging((prev) => {
        const next = new Set(prev);
        next.delete(violationId);
        return next;
      });
    }
  };

  const unacknowledgedCount = violations.filter(
    (v) => !v.acknowledged
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("geofence.violationsTitle", "Geofence Violations")}
            </h2>
            <p className="text-sm text-gray-600">
              {t("geofence.violationsSubtitle", "Monitor and acknowledge boundary violations")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unacknowledgedCount > 0 && (
            <div className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm font-semibold">
              {t("geofence.unacknowledgedCount", "{n} unacknowledged").replace("{n}", String(unacknowledgedCount))}
            </div>
          )}
          <button
            onClick={() => fetchViolations()}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={cn("h-4 w-4", refreshing && "animate-spin")}
            />
            {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          {t("geofence.filterByGeofence", "Filter by Geofence")}
        </label>
        <select
          value={selectedGeofenceId}
          onChange={(e) => setSelectedGeofenceId(e.target.value)}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        >
          <option value="">{t("geofence.allGeofences", "All Geofences")}</option>
          {geofences.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      {/* Violations Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : violations.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <CheckCircle className="h-12 w-12 text-emerald-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900">
              {t("geofence.noViolations", "No violations detected")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("geofence.noViolationsHint", "All devices are within their designated geofences.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("domain.device", "Device")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("geofence.geofenceHeader", "Geofence")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("common.time", "Time")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("geofence.gpsCoordinates", "GPS Coordinates")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("common.status", "Status")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {t("geofence.actionHeader", "Action")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {violations.map((violation) => (
                  <tr
                    key={violation.id}
                    className={cn(
                      "transition-colors",
                      !violation.acknowledged
                        ? "bg-red-50 hover:bg-red-100"
                        : "hover:bg-gray-50"
                    )}
                  >
                    <td className="px-6 py-4">
                      <span className="font-mono font-medium text-gray-900">
                        {violation.serial_number}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {violation.geofence_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatTime(violation.created_at, t, lang)}
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={`https://www.google.com/maps?q=${violation.gps_lat},${violation.gps_lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-mono text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
                      >
                        <MapPin className="h-3 w-3" />
                        {Number(violation.gps_lat).toFixed(5)},
                        {Number(violation.gps_lng).toFixed(5)}
                      </a>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold",
                          violation.acknowledged
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        )}
                      >
                        {violation.acknowledged ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {violation.acknowledged ? t("geofence.acknowledged", "Acknowledged") : t("geofence.unacknowledged", "Unacknowledged")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!violation.acknowledged && (
                        <button
                          onClick={() => handleAcknowledge(violation.id)}
                          disabled={acknowledging.has(violation.id)}
                          className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {acknowledging.has(violation.id)
                            ? t("geofence.acknowledging", "Acknowledging...")
                            : t("geofence.acknowledge", "Acknowledge")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function getAuthHeaders(user: any) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("auth_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (user?.email) headers["x-user-email"] = user.email;
  return headers;
}

function formatTime(
  dateStr: string,
  t: (key: string, fallback?: string) => string,
  lang: LangCode
) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return t("geofence.justNow", "Just now");
  if (diffMins < 60) return t("geofence.minutesAgo", "{n}m ago").replace("{n}", String(diffMins));

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t("geofence.hoursAgo", "{n}h ago").replace("{n}", String(diffHours));

  return new Intl.DateTimeFormat(lang, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
